import { TrackerEventType } from "src/core/model/TrackerEventType";
import { TrackerVariableType } from "src/core/model/TrackerVariableType";

import { EventListener } from "./EventListener";
import { NativeEventTrigger } from "./PageViewListener";

const ACTIVITY_EVENTS = ["mousemove", "keydown", "scroll", "touchstart", "click"] as const;

/**
 * Triggers IDLE event when the user has been inactive for a configurable duration.
 * Resets the timer on any user interaction. Uses cooldown to avoid repeated
 * IDLE events during prolonged inactivity.
 */
export class IdleListener implements EventListener {
  private trigger: NativeEventTrigger;
  private started = false;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private boundActivityHandler: (() => void) | null = null;
  private idleThresholdMs: number;
  private cooldownMs: number;
  private lastTriggerTs = 0;

  constructor(trigger: NativeEventTrigger, idleThresholdMs = 30_000, cooldownMs = 60_000) {
    this.trigger = trigger;
    this.idleThresholdMs = idleThresholdMs;
    this.cooldownMs = cooldownMs;
  }

  start(): void {
    if (this.started) return;
    this.started = true;

    this.boundActivityHandler = () => this.resetTimer();

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, this.boundActivityHandler, { passive: true });
    }

    this.resetTimer();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;

    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    if (this.boundActivityHandler) {
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, this.boundActivityHandler);
      }
      this.boundActivityHandler = null;
    }
  }

  getTypes(): TrackerEventType[] {
    return [TrackerEventType.IDLE];
  }

  getVariableTypes(): TrackerVariableType[] {
    return [];
  }

  private resetTimer(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);

    this.idleTimer = setTimeout(() => {
      const now = Date.now();
      if (now - this.lastTriggerTs >= this.cooldownMs) {
        this.lastTriggerTs = now;
        this.trigger(TrackerEventType.IDLE);
      }
    }, this.idleThresholdMs);
  }
}
